<template>
  <div class="payment-dialog-content input-text-size">
    <Loader v-if="fortePaymentSuscessFlag"/>
    <!-- HEADER - the payment's own information, and nothing else. One card: two rows of fields
         on the left, the display-only totals panel on the right. The tax-record lookup fields
         that load the grid sit down on the record list they load, not up here. -->
    <div class="payment-header-card">
      <div class="payment-header-fields">
        <v-row no-gutters class="payment-header-row align-center">
          <v-col cols="2" class="pr-4">
            <!-- reads "New" until the payment is saved and the number comes back -->
            <v-text-field
              class="input-text-size payment-dialog-payment-number"
              id="paymentDialogPaymentNumber"
              :value="paymentNumberDisplay"
              label="Payment #"
              readonly
              tabindex="-1" />
          </v-col>
          <!-- Editable while the payment is being entered - it opens on the dashboard's
               "Business Date for payment entry" (tax/taxPayment.paymentDialogBusinessDate, which
               is what paymentTransactionList stamps on a new payment), and the clerk can still
               change it on THIS payment. Locked once the payment is posted: the date it was
               posted on is history.
               readonly rather than disabled for that locked state, so the date stays legible and
               selectable (and takes the same grey as Payment # beside it) instead of being
               painted at Vuetify's 38% opacity - and out of the tab order while it is locked, so
               tabbing through the header skips what cannot be changed. -->
          <v-col cols="2" class="pr-4">
            <ClarionDateControl
              class="input-text-size payment-dialog-business-date"
              id="paymentDialogBusinessDate"
              label="Business Date"
              :isToggle="false"
              :rules="businessDateRules"
              :isRequired="!checkIfTaxDetailsPosted()"
              v-model="editedItem.payDate"
              :isReadOnly="checkIfTaxDetailsPosted()"
              :tabIndex="checkIfTaxDetailsPosted() ? -1 : null" />
          </v-col>
          <v-col cols="2" class="pr-4">
            <v-select
              class="input-text-size"
              id="tax-check-recon-criteria-wkst"
              label="Workstation #"
              v-model="editedItem.workstation"
              :items="getPaymentWkstNumList()"
              :readonly="checkIfTaxDetailsPosted()"
              :tabindex="checkIfTaxDetailsPosted() ? -1 : 0" />
          </v-col>
          <v-col cols="2" class="pr-4">
            <v-text-field
              class="input-text-size payment-dialog-fiscal-year"
              id="paymentDialogFiscalYear"
              :value="editedItem.fiscalYear"
              label="Fiscal Year"
              readonly
              tabindex="-1" />
          </v-col>
          <!-- Paid By takes the rest of the row - it holds a name, and it was the one field
               here that had no label of its own. -->
          <v-col cols="4">
            <v-text-field
              class="input-text-size payment-dialog-paid-by"
              id="paymentDialogPaidBy"
              label="Paid By"
              placeholder="Name on the check or receipt"
              persistent-placeholder
              :value="getPaidBy()" />
          </v-col>
        </v-row>
        <!-- Who entered the payment and when (createdBy / createdWhen, from the audit record).
             Read-only history, so neither field is in the tab order. A payment that has not been
             saved yet has no audit record - the row is absent then rather than showing two empty
             boxes. -->
        <v-row
          v-if="editedItem.createdBy || editedItem.createdWhen"
          no-gutters
          class="payment-header-row align-center">
          <v-col cols="3" class="pr-4">
            <v-text-field
              class="input-text-size payment-dialog-created-by"
              id="paymentDialogCreatedBy"
              :value="editedItem.createdBy"
              label="User Name"
              readonly
              tabindex="-1" />
          </v-col>
          <v-col cols="3" class="pr-4">
            <v-text-field
              class="input-text-size payment-dialog-created-when"
              id="paymentDialogCreatedWhen"
              :value="createdWhenDisplay"
              label="Date/Time"
              readonly
              tabindex="-1" />
          </v-col>
        </v-row>
        <!-- The payment's flags and dates. The two either/or pairs are segmented toggles rather
             than switches: a switch only ever showed the state it was in, so "Normal" and
             "Full Pay" read as the labels of an unlabelled control. Both still write through
             onChangeHalfOrPostmark, so the store sees exactly what the switches sent. -->
        <v-row no-gutters class="payment-header-row payment-header-row--flags align-center">
          <v-col cols="auto" class="pr-4">
            <BinaryToggle
              class="payment-dialog-postmark-toggle"
              id="paymentDialogPostmarkToggle"
              off-label="Normal"
              on-label="Postmark OK"
              :value="halfOrPostmark.dialogPostmark"
              @input="isOn => onChangeHalfOrPostmark(isOn, 'dialogPostmark')" />
          </v-col>
          <v-col cols="auto" class="pr-4">
            <BinaryToggle
              class="payment-dialog-half-toggle"
              id="paymentDialogHalfToggle"
              off-label="Full"
              on-label="Half"
              :value="halfOrPostmark.dialogHalf"
              @input="isOn => onChangeHalfOrPostmark(isOn, 'dialogHalf')" />
          </v-col>
          <v-col cols="auto" class="pr-4">
            <div class="payment-header-rule" />
          </v-col>
          <v-col cols="auto" class="pr-4">
            <v-checkbox
              class="payment-dialog-void-all mt-0"
              label="Void All"
              dense
              hide-details
              @change="isVoidChecked"
              v-model="editedItem.isVoid"
              :disabled="editedItem.isVoid" />
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              class="payment-dialog-mail mt-0"
              label="Mail"
              dense
              hide-details
              v-model="editedItem.mail" />
          </v-col>
          <v-spacer />
          <v-col cols="auto" class="payment-header-penalty-date">
            <ClarionDateControl
              class="input-text-size payment-dialog-penalty-date"
              id="paymentDialogPenaltyDate"
              label="Penalty Date"
              :isToggle="false"
              v-model="editedItem.penaltyDate"
              @input="handlePenDateInput" />
          </v-col>
        </v-row>
      </div>
      <!-- Totals: figures the clerk reads, never keys. Rendered as text so they stop looking
           like the inputs beside them. Change Due is still whatever totalPaymentAmount worked
           out and put on the payment. -->
      <div class="payment-header-totals">
        <div class="payment-totals-legend">TOTALS</div>
        <div class="payment-totals-line">
          <span class="payment-totals-label">Total Due</span>
          <span
            class="payment-totals-value payment-dialog-total-due"
            id="paymentDialogTotalDue"
            :key="changeKeyOnDialog">{{ totalSum }}</span>
        </div>
        <div class="payment-totals-line">
          <span class="payment-totals-label">Total Paid</span>
          <span
            class="payment-totals-value payment-dialog-total-paid"
            id="paymentDialogTotalPaid"
            :key="changeKeyOnDialog">{{ totalPaidDisplay }}</span>
        </div>
        <div class="payment-totals-line">
          <span class="payment-totals-label">Change Due</span>
          <span
            class="payment-totals-value payment-dialog-cash-change"
            id="paymentDialogChangeDue">{{ changeDueDisplay }}</span>
        </div>
      </div>
    </div>
    <!-- <template>
      <dialogTaxDetails
        ref="dialogTaxDetailsRef"
        v-model="editedItem.taxDetails"
        :baseCrudKey="baseCrudKey"
        :fiscalYear="editedItem.fiscalYear"
        :loadMoreTaxBtn="editedItem.loadMoreTax" />
    </template> -->
    <!-- THE RECORD LIST, and the strip that loads it. The band is the list's own top edge -
         tinted, sharing its bottom edge with the grid - so the Tax Year / Tax ID / Parcel ID
         fields read as the loader for the rows underneath rather than as more of the payment
         header above. Nothing about what they do changed; only where they sit. -->
    <div class="record-list-card">
      <div class="add-tax-records-band">
        <div class="add-tax-records-legend">
          <v-icon small>mdi-magnify</v-icon>
          <span class="add-tax-records-legend-text">ADD TAX<br>RECORDS</span>
        </div>
        <!-- Type: the payment type every record loaded from this band is created as. It comes
             FIRST, before the tax year and tax id, because that is where the old dialogTaxDetails
             grid had it - the row's first column - and so a refund is keyed as a refund rather
             than entered as a payment and corrected in TaxEditDialog afterwards.
             Sticky the way the Tax Year is: a clerk working a run of refunds sets it once. Reset
             to 1 Tax Payment when the dialog closes, so the next payment starts back at 1. -->
        <div
          class="add-tax-records-field add-tax-records-field--type"
          :class="{ 'add-tax-records-field--alt': isNonPaymentTypeKeyed }">
          <v-select
            v-model="taxTypeLookup"
            class="input-text-size payment-dialog-tax-type-lookup"
            id="paymentDialogTaxTypeLookup"
            ref="taxTypeLookupRef"
            label="Type"
            :items="taxPaymentTypeOptions"
            item-text="label"
            item-value="value"
            hide-details
            @keydown="handleTaxTypeKeydown" />
        </div>
        <!-- Tax Yr + Tax ID lookup: resolves the pair and loads that tax record as a row in the
             large payment grid below. Neither value is persisted with the payment - they are the
             keyed criteria only. A Tax ID of 8+ digits is treated as a parcel ID, and an 11-digit
             value keyed in one go (a scanned barcode) splits into year + id. -->
        <div class="add-tax-records-field add-tax-records-field--year">
          <v-text-field
            v-model="taxYearLookup"
            class="input-text-size payment-dialog-tax-year-lookup"
            id="paymentDialogTaxYearLookup"
            ref="taxYearLookupRef"
            label="Tax Year"
            hide-details
            :loading="taxRowLookupLoading"
            @input="handleBarCodeScan"
            @blur="loadTaxRowByYearAndId()"
            @keydown.enter.prevent.stop="loadTaxRowByYearAndId()"
            @focus="$event.target.select()" />
        </div>
        <div class="add-tax-records-field">
          <v-text-field
            v-model="taxIdLookup"
            class="input-text-size payment-dialog-tax-id-lookup"
            id="paymentDialogTaxIdLookup"
            ref="taxIdLookupRef"
            label="Tax ID"
            placeholder="Tax ID"
            persistent-placeholder
            hide-details
            :loading="taxRowLookupLoading"
            @blur="loadTaxRowByYearAndId()"
            @keydown.enter.prevent.stop="loadTaxRowByYearAndId()"
            @focus="$event.target.select()" />
        </div>
        <!-- Lookup-only control: resolves a parcel to every unpaid tax record on it and loads them
             as rows in the grid below. Never persisted with the payment. Whichever grid is
             rendered owns the load, so this field delegates to it - dialogTaxDetails on the June
             Resale branch, LargePaymentList everywhere else. -->
        <div class="add-tax-records-field">
          <v-text-field
            v-model="parcelLookupId"
            class="input-text-size payment-dialog-parcel-lookup"
            id="paymentDialogParcelLookup"
            ref="parcelLookupRef"
            label="Parcel ID"
            placeholder="Parcel ID"
            persistent-placeholder
            hide-details
            :autofocus="defaultCursorToParcelID"
            :loading="parcelLookupLoading"
            @input="parcelLookupByTimeOut"
            @keydown.enter.prevent.stop="loadUnpaidsByParcelId()"
            @blur="loadUnpaidsByParcelId()" />
        </div>
        <!-- The mockup's LOAD button is deliberately left out: tabbing off either field, Enter
             and a barcode scan all already run the load, so the button would be a second way in
             to what leaving the field does. The hint below says so. -->
        <div class="add-tax-records-hint">
          Pick a Type (or key its number), then key a tax year + tax ID or Parcel ID and press tab
          to load record(s) into list below. Or scan a barcode.
        </div>
      </div>
      <template v-if="checkIfTaxDetailsPosted() && editedItem.taxDetails.length >= 30">
        <div class="large-payment-wrapper">
          <LargePaymentList
            ref="largePaymentListRef"
            :key="`large-payment-${editedItem.paymentNumber || 'new'}-${editedItem.fiscalYear || ''}`"
            :fiscalYear="editedItem.fiscalYear"
            :paymentNumber="editedItem.paymentNumber"
            :taxDetails="editedItem.taxDetails"
            :parentCrudKey="taxGridParentCrudKey"
            :isNewPayment="isNewPaymentEntry"
            :totalDue="totalSum"
            :totalPaid="totalPaidMain"
            :cashChange="editedItem.cashChange"
            :totalSumMethod="totalSum"
            @totalChanged="handleTotalChanged"
            @taxDetailsChanged="handleTaxDetailsChanged"
            @selectionChanged="handleTaxDetailsSelectionChange"
            @serverTotalsChanged="handleServerTotalsChanged"
            @warningsAnswered="placeInitialCursor"
            :checkIfTaxDetailsPosted="checkIfTaxDetailsPosted()" />
        </div>
      </template>
      <!-- A new payment renders the list view below with the rest of them now - the payment
           screen enters a payment on the same grid the picklist and existing payments already
           use, keying its rows through the Add Tax Records band's Tax Year / Tax ID and Parcel
           ID lookups rather than on a blank row in this grid.
           June Resale is the exception, and stays here: the resale fee it adds to the record for
           the resale tax year, and the penalty it carries over rather than recalculating, are
           implemented in this component alone (selectedComponentCriteria / isItFromJuneResale).
           -->
      <template
        v-else-if="isItFromJuneResale && baseCrudKey !== 'pickListMakePaymentDialog'
          && ((editedItem.paymentNumber || 0) === 0)">
        <dialogTaxDetails
          ref="dialogTaxDetailsRef"
          v-model="editedItem.taxDetails"
          :baseCrudKey="baseCrudKey"
          :fiscalYear="editedItem.fiscalYear"
          :loadMoreTaxBtn="editedItem.loadMoreTax"
          :isItFromJuneResale="isItFromJuneResale"
          :selectedComponentCriteria="selectedComponentCriteria"
          @selectionChanged="handleTaxDetailsSelectionChange"
          @parcelLookupConsumed="clearParcelLookup" />
      </template>
      <template v-else>
        <div class="large-payment-wrapper">
          <LargePaymentList
            ref="largePaymentListRef"
            :key="largePaymentKey"
            :fiscalYear="editedItem.fiscalYear"
            :paymentNumber="editedItem.paymentNumber"
            :taxDetails="editedItem.taxDetails"
            :parentCrudKey="taxGridParentCrudKey"
            :isNewPayment="isNewPaymentEntry"
            :totalDue="totalSum"
            :totalPaid="totalPaidMain"
            :cashChange="editedItem.cashChange"
            :totalSumMethod="totalSum"
            @totalChanged="handleTotalChanged"
            @taxDetailsChanged="handleTaxDetailsChanged"
            @selectionChanged="handleTaxDetailsSelectionChange"
            @serverTotalsChanged="handleServerTotalsChanged"
            @warningsAnswered="placeInitialCursor"
            :checkIfTaxDetailsPosted="checkIfTaxDetailsPosted()" />
        </div>
      </template>
    </div>
    <dialogPaymentDetails id="miscEditDialogPaymentDetails"
      class="misc-edit-dialog-payment-details input-text-size"
      toolbarTitle="Payment Details"
      ref="paymentDetailsRef"
      v-model="editedItem.paymentDetails"
      :isTotal="true"
      :cmnEnums="cmnEnums"
      :defaultForteConfig="defaultForteConfig"
      :amount="totalSum"
      :taxPaymentRecord="editedItem"
      :showOverUnder="showOverUnder"
      :showPennyRound="showPennyRound"
      :showOverpayment="showOverpayment"
      :overpaymentAmt="overpaymentAmt"
      :programType="'TAX'"
      @totalPaymentAmount="totalPaymentAmount"
      @overUnderPayClicked="overUnderPayClicked"
      @pennyRoundClicked="pennyRoundClicked"
      @overpaymentClicked="overpaymentClicked"
      @paymentFieldActivity="handlePaymentFieldActivity" />
    <CommonPopUp v-model="showFiscalYearErrorMsg" v-if="showFiscalYearErrorMsg"
      :enableFlashTypeMsg="false">
      <template v-slot:customsLabelOrInfo>
        <h3 class="mt-3">Cannot move to different Fiscal year</h3>
        <p class="mt-3">This payment transaction was created in FY {{ editedItem.fiscalYear }},
          and cannot be moved to different Fiscal year.
          Please void or cancel the transaction and re-enter it in the desired fiscal year.
        </p>
      </template>
      <template v-slot:[`customsButtonsForPopUp`]="{}">
        <v-btn class="input-text-size" text color="blue darken-1"
        @click="closeFiscalYearErrorMsg()">OK</v-btn>
      </template>
    </CommonPopUp>
</div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import dialogPaymentDetails from './dialogPaymentDetails.vue';
// only the June Resale branch mounts this now - every other payment uses LargePaymentList
import dialogTaxDetails from './dialogTaxDetails.vue';
// header Tax Yr / Tax ID -> the tax-details grid's own lookup, kept out of that component
import { loadTaxRowViaTaxDetailsGrid, onWarningPopupsAnswered } from './headerTaxRowLookup.util';
// import LargePaymentTransactionList from '
// ../../../../views/tax/payments/largePayments/largePaymentTransactionList.vue';
import '../../../../assets/style.css';
import {
  isFiscalYearWithinPostingPeriod, toFiscalYear, toIsoDate, toShortDate,
} from '../../../../util/shared/tmc-global';
import {
  amountFormat,
  amountToNumber,
  isAltP,
  isAltR,
  isFnNineKey,
  isFnTenKey,
  isInsert,
  isTaxOrPayFnKey,
  toLocalDateTime,
} from '../../../../util/shared/vue-global';
import LargePaymentList from '../../../../views/tax/payments/largePayments/LargePaymentList.vue';
import BinaryToggle from '../../../common/base/BinaryToggle.vue';
import ClarionDateControl from '../../../common/ClarionDateControl.vue';
import CommonPopUp from '../../common-utils/CommonPopUp.vue';
import { getFeeListBasedOnEffeciveDate, getPreviousPenaltyPeriodEnd } from '../../common-utils/CommonTaxFunctionalities';
import { commonVoidFunc } from '../../common-utils/CommonTaxPaymentFunctionalities';
import { SEARCH_DEBOUNCE_TIMER } from '../../common-utils/constants';
import Loader from '../../common-utils/loader.vue';
import {
  buildTaxPaymentTypeOptions,
  DEFAULT_TAX_PAYMENT_TYPE,
  exemptManufacturingStatusCode,
  needsPennyRounding,
  parseLocalDate,
  payDateBeforePostingPeriodMsg,
  penaltyCalculation,
  pennyRoundingFeeAmount,
} from '../../common-utils/tax-common.util';

// A scanner appends its suffix (CR, CR+LF or Tab) to the barcode. Those normally arrive as
// keystrokes, but a scanner set to send them as characters types them into the field instead,
// where they would fail the 11-digit test. Only the digits are the barcode.
const scannedDigits = (value) => String(value || '').replace(/\D/g, '');

export default {
  props: {
    baseCrudKey: {
      type: String,
      default: 'paymentTransactionList',
    },
    attachmentConstants: {
      type: Object,
      default: () => { },
    },
    isItFromJuneResale: {
      type: Boolean,
      default: false,
    },
    selectedComponentCriteria: {
      type: Object,
      default: () => { },
    },
  },
  data() {
    return {
      delayTimerId: null,
      totalPaidMain: 0.00,
      showOverUnder: false,
      showPennyRound: false,
      showOverpayment: false,
      remainingBalance: 0,
      // The Business Date the payment had when the dialog opened. A payment already saved is only
      // held to the posting-period rule if the clerk CHANGES this - see businessDateRules.
      payDateAtOpen: null,
      showFiscalYearErrorMsg: false,
      hasSelectedTaxDetails: false,
      selectedTaxDetailIds: [], // Store selected item IDs to preserve selection
      // Server totals from LargePaymentList (accurate across all pages)
      serverTotalDue: null,
      // Parcel ID lookup field. Local-only: it is never emitted with the payment, so it cannot
      // reach taxpayments / paymenttaxdetails / audits.
      parcelLookupId: '',
      parcelLookupLoading: false,
      parcelLookupTimerId: null,
      // Tax Yr / Tax ID lookup fields - keyed criteria only, same as the Parcel ID field above.
      taxYearLookup: '',
      taxIdLookup: '',
      /**
       * The band's Type selector: the payment type every record the band loads is created as.
       * Not criteria - it is written onto each row as taxType - and deliberately NOT cleared
       * after a load, so a clerk can key a run of refunds. The dialog closing puts it back to
       * 1 Tax Payment (see the isVisibleProp watcher).
       */
      taxTypeLookup: DEFAULT_TAX_PAYMENT_TYPE,
      taxRowLookupLoading: false,
      barcodeScanTimerId: null,
      // disposer for the watch on dialogTaxDetails' warning popups (see onWarningPopupsAnswered)
      disposeWarningWatch: null,
      changeKeyOnDialog: 1,
      defaultForteConfig: {
        configScope: 'paymentConfig',
        isRequiredForte: true,
      },
      prePostmarkPenaltyDate: null,
    };
  },
  components: {
    dialogTaxDetails,
    BinaryToggle,
    CommonPopUp,
    dialogPaymentDetails,
    ClarionDateControl,
    // LargePaymentTransactionList,
    LargePaymentList,
    Loader,
  },
  created() {
    window.addEventListener('keydown', this.handlePayShortCut);
  },
  mounted() {
    // The dialog is already open the first time this renders - v-dialog builds its content
    // lazily - so the isVisibleProp watcher never sees that first transition.
    this.seedTaxYearLookup();
    this.placeInitialCursor();
  },
  beforeDestroy() {
    // a pending lookup must not fire after the dialog is gone
    clearTimeout(this.parcelLookupTimerId);
    clearTimeout(this.barcodeScanTimerId);
    if (this.disposeWarningWatch) this.disposeWarningWatch();
  },
  destroyed() {
    window.removeEventListener('keydown', this.handlePayShortCut);
  },
  computed: {
    ...mapGetters('base/crud', [
      'item',
      'items',
      'index',
      'isVisible',
    ]),
    ...mapGetters(['todaysDate']),
    ...mapState({
      nextReceiptNumber: (state) => state.misc.miscTransaction.nextReceiptNumber,
      cmnEnums: (state) => ({
        cmnPaymentTypes: state.enums.TaxPaymentTypeEnum || [],
        cmnEFTTypes: state.enums.MISCEFTTypeEnum || [],
        cmnCardTypes: state.enums.MISCCardTypeEnum || [],
      }),
      fortePaymentSuscessFlag: (state) => state.ForteService.fortePaymentSuscessFlag,
      paymentWkstNumList: (state) => state.tax.taxPayment.paymentWkstNumList || [],
      miscSettingItem: (state) => state.SystemConfig.miscSettingItem || {},
      nextTransNumber: (state) => state.common.cmnTransNumber.nextTransNumber,
      transNoPulse: (state) => state.common.cmnTransNumber.transNoPulse,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem,
      taxFeeList: (state) => state.base.crud.items.sharedTaxFeeMaster,
      halfOrPostmark: (state) => state.tax.taxPayment.halfOrPostmark,
      paymentShotCutStore: (state) => state.tax.taxPayment.paymentShotCutStore,
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem,
      taxFeeMasterData: (state) => state.shared.taxFeeMaster.taxFeeMasterData,
      // fallback for the header's Tax Year default, same as dialogTaxDetails uses for a new row
      latestTaxYear: (state) => state.tax.taxCommon.latestTaxYear,
      // the band's Type selector offers the same list the Type on the line does
      statusTypes: (state) => state.enums.TaxPaymentEnum || [],
    }),
    ...mapState('tax/ccPayments', ['paymentSuccessFlag']),
    editedItemProp() {
      return this.editedItem;
    },
    editedItem: {
      get() {
        const item = this.item(this.baseCrudKey);
        return item;
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    totalSum() {
      // If we have server totals from LargePaymentList (for paginated data), use them
      if (this.serverTotalDue !== null) {
        return amountFormat(this.serverTotalDue || 0);
      }

      // Fallback: Calculate from current taxDetails (for non-paginated or picklist mode)
      let total = 0;
      if (this.editedItem.taxDetails && this.editedItem.taxDetails.length > 0) {
        // Include items that are TAXPAYMENT or don't have taxType set (new items)
        total = this.editedItem.taxDetails
          .reduce((acc, item) => {
            if (!item.taxType || item.taxType === 'TAXPAYMENT') {
              return acc + amountToNumber(item.total || 0);
            }
            return acc;
          }, 0);
      }
      return amountFormat(total || 0);
    },
    /**
     * The Business Date's validation - while the field is editable, and only then.
     *
     * A POSTED payment's date is history, and an old one is routinely outside today's posting
     * period, so the second rule printed "The Date results in a fiscal year outside the current
     * posting period" in red under a reopened payment: true, not news, and nothing the clerk can
     * act on from there. On entry it is worth having, and it is the same check the dashboard's
     * own "Business Date for payment entry" runs (largePaymentCriteria.businessRules).
     */
    businessDateRules() {
      if (this.checkIfTaxDetailsPosted()) return [];
      return [
        (v) => !!v || 'Required.',
        (v) => !v || isFiscalYearWithinPostingPeriod(toFiscalYear(parseLocalDate(v))) || 'The Date results in a fiscal year outside the current posting period',
        // Refuses a date BEFORE the open posting period (Tulsa 2023-191970: 07/01 keyed while October
        // was open - it saved, could never be posted, and sat unseen for 18 days). Dates after the
        // period end stay allowed: counties key next month's payments early. Every save path is gated
        // on the form validating. A payment already saved keeps the date it has, so voiding or
        // reprinting one in a closed month still works; the server is the authority either way.
        (v) => {
          const message = payDateBeforePostingPeriodMsg(v, this.taxSettingItem);
          if (!message) return true;
          if (this.editedItem.paymentNumber) {
            // Saved payment: only a CHANGED date is refused. An unknown starting date is left to the server.
            if (!this.payDateAtOpen || toIsoDate(v) === toIsoDate(this.payDateAtOpen)) return true;
          }
          return message;
        },
      ];
    },
    isVisibleProp: {
      get() {
        return this.isVisible(this.baseCrudKey);
      },
    },
    largePaymentKey() {
      // Use a stable key that doesn't change when tax details are modified
      // This prevents Vue from re-creating the component and losing serverTotals state
      const fiscalYear = this.editedItem.fiscalYear || '';
      const paymentNumber = this.editedItem.paymentNumber || 'new';
      return `large-payment-picklist-${fiscalYear}-${paymentNumber}`;
    },
    createdWhenDisplay() {
      const when = this.editedItem.createdWhen;
      return when ? toLocalDateTime(when) : '';
    },
    /**
     * What the header's Payment # reads. The number only exists once the payment is saved, so
     * until then the field said 0 (or nothing at all on a payment that has no number yet) -
     * it reads "New" instead, which is what the clerk is looking at.
     */
    paymentNumberDisplay() {
      const paymentNumber = this.editedItem.paymentNumber || 0;
      return paymentNumber ? String(paymentNumber) : 'New';
    },
    /**
     * The two lower totals. As text fields they were whatever was assigned to them - 0 before
     * a payment line is keyed, 0.00 after - which reads as two different numbers sitting under
     * Total Due. Formatted the same way Total Due (totalSum) already is.
     */
    totalPaidDisplay() {
      return amountFormat(amountToNumber(this.totalPaidMain) || 0);
    },
    changeDueDisplay() {
      return amountFormat(amountToNumber(this.editedItem.cashChange) || 0);
    },
    /**
     * A payment being entered on the payment screen, before it has a number. This is the case
     * that used to render dialogTaxDetails; it renders the list view now, which has nothing to
     * load for it - its rows are whatever the caller seeded onto the payment plus whatever the
     * header lookups append. (June Resale is the one such payment that still renders the old
     * grid, and so never reads this.)
     */
    isNewPaymentEntry() {
      return this.baseCrudKey !== 'pickListMakePaymentDialog'
        && (this.editedItem.paymentNumber || 0) === 0;
    },
    /**
     * Whether an Add Comment button is offered. The button itself now sits in the grid's own
     * footer row, between Mtg Code and the Running Total - the header is the payment's
     * information only - so nothing in this template reads this; it stays as the answer to
     * "does this dialog have a comment button", which is the grid-delegation path below
     * (openAddComment) and its tests.
     * The comment dialog belongs to LargePaymentList, which renders on every branch below except
     * June Resale's - and even there the 30+-posted branch wins and renders it. Mirrors those two
     * conditions rather than probing $refs, which is not reactive.
     */
    showAddCommentBtn() {
      const postedLargeGrid = this.checkIfTaxDetailsPosted()
        && ((this.editedItem.taxDetails || []).length >= 30);
      const juneResaleGrid = this.isItFromJuneResale
        && this.baseCrudKey !== 'pickListMakePaymentDialog'
        && (this.editedItem.paymentNumber || 0) === 0;
      return postedLargeGrid || !juneResaleGrid;
    },
    /**
     * The crud key the list view writes the rows back onto.
     * Only the new-payment case gets this dialog's own key: on the picklist and existing-payment
     * branches the grid loads the payment from the API and writes what came back, which on the
     * payment screen's key would overwrite the transaction being edited (paymentDetails and all)
     * with the response. Those keep the key the grid has always used.
     */
    taxGridParentCrudKey() {
      return this.isNewPaymentEntry ? this.baseCrudKey : 'pickListMakePaymentDialog';
    },
    // 'Default cursor to Parcel ID' (payment settings). The dialog opens with the cursor in the
    // Parcel ID lookup rather than the first row's Tax Yr, so a parcel-driven clerk can key
    // straight into it. Off - the default - leaves the cursor where it has always been.
    defaultCursorToParcelID() {
      return (this.paymentSettingItem || {}).defaultCursorToParcelID || false;
    },
    /**
     * The Tax Year the header lookup opens on, so the clerk only keys the Tax ID. Same source
     * and same precedence dialogTaxDetails.addOne uses for a new row's Tax Yr: the tax setting's
     * year, then the latest tax year loaded.
     */
    defaultTaxYearForLookup() {
      return Number((this.taxSettingItem || {}).taxYear) || this.latestTaxYear || '';
    },
    /**
     * The Type selector's options - 1 Tax Payment ... 7 Other, the same list and the same
     * numbering the Type on the line offers (buildTaxPaymentTypeOptions).
     */
    taxPaymentTypeOptions() {
      return buildTaxPaymentTypeOptions(this.statusTypes);
    },
    /**
     * Whether the band is set to something other than 1 Tax Payment, which is what puts the
     * amber on the selector. The type is sticky, so without it a run of refunds is keyed into a
     * band that looks exactly like a run of payments.
     */
    isNonPaymentTypeKeyed() {
      return this.taxTypeLookup !== DEFAULT_TAX_PAYMENT_TYPE;
    },
    overpaymentAmt() {
      const details = this.editedItem.paymentDetails || [];
      for (let i = 0; i < details.length; i += 1) {
        if (details[i].overpaymentAmt) {
          return details[i].overpaymentAmt;
        }
      }
      return 0;
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setItem',
      'setItems',
      'setIsVisible',
    ]),
    ...mapMutations('tax/taxPayment', [
      'setHalfOrPostmark',
      'setPaymentShotCutStore',
    ]),
    ...mapActions('tax/taxPayment', [
      'loadMoreTax',
    ]),
    getPaymentWkstNumList() {
      if ((this.paymentWkstNumList || []).length) {
        const currentWorkstation = Number(this.editedItem.workstation) || null;
        // if the workstation is already in the list, return the list
        if (this.paymentWkstNumList.includes(currentWorkstation)) {
          return this.paymentWkstNumList;
        }
        // if the workstation is not in the list and is not null/undefined, add it to the list
        if (currentWorkstation != null) {
          const combinedList = [...this.paymentWkstNumList, currentWorkstation];
          combinedList.sort((a, b) => a - b);
          return combinedList;
        }
        return this.paymentWkstNumList;
      } return [];
    },
    checkIsUndefined() {
      if (this.editedItem.paymentDetails && this.editedItem.paymentDetails.length > 0) return true;
      return false;
    },
    openDialog() {
      this.isVisibleProp = true;
      // The blank first row only exists on the June Resale branch now - the list view opens
      // empty and takes its rows from the Add Tax Records band's Tax Yr / Tax ID and Parcel ID
      // lookups, so this is a no-op wherever that grid is the one rendered.
      this.$nextTick(() => {
        if (this.$refs.dialogTaxDetailsRef) {
          const hasValues = this.$refs.dialogTaxDetailsRef.hasValues();
          if (!hasValues) {
            this.$refs.dialogTaxDetailsRef.addOne();
          }
        }
      });
    },
    handlePayShortCut(e) {
      const dialogIsOpenOrNot = this.isVisible(this.baseCrudKey);
      if (dialogIsOpenOrNot) {
        const isInsertRes = isInsert(e) || isTaxOrPayFnKey(e);
        const isInsertR = isAltR(e) || isFnTenKey(e); // to Save & Print Receipt
        const isInsertP = isAltP(e); // jumps the cursor to the bottom payment section.
        const isTaxOrPayFnKeyRes = isTaxOrPayFnKey(e);
        if (isTaxOrPayFnKeyRes) e.preventDefault();
        // checking for F11 or ins keys
        if (isInsertRes || isTaxOrPayFnKeyRes) {
          // check which to add is it payment or tax record
          if (this.paymentShotCutStore.paymentAddIconStatus) {
            this.setPaymentShotCutStore({ res: this.paymentShotCutStore.isItPayRecordNCounter + 1, whichOne: 'isItPayRecordNCounter' });
          } else this.setPaymentShotCutStore({ res: this.paymentShotCutStore.isItTaxRecordNCounter + 1, whichOne: 'isItTaxRecordNCounter' });
        }
        if (isInsertP) {
          const defaultPayItem = {
            paymentType: '',
            amount: 0,
            paidBy: '',
          };
          // if there is no payment-details adding one
          if (!this.editedItem.paymentDetails.length) {
            this.editedItem.paymentDetails = [defaultPayItem];
          }
          this.$refs.paymentDetailsRef.highlightTypeFunc(this.editedItem.paymentDetails.length
           || 0);
          // pointing to payment-details, used to highlight plus icon
          this.setPaymentShotCutStore({ res: true, whichOne: 'paymentAddIconStatus' });
        }
        if (isInsertR) this.$emit('callFromPayDialog', true);
        if (isFnNineKey(e)) {
          e.preventDefault();
          this.toggleHalfPayFromKeyboard(e);
        }
      }
    },
    // F9 flips Full Pay / Half Pay exactly as clicking the dialog switch does, but through the
    // store rather than the switch element, so focus (e.g. the Tax Year field) stays put.
    // Like the switch, it only affects rows added or edited afterwards.
    toggleHalfPayFromKeyboard(e) {
      if (e && e.repeat) return; // holding F9 down must not flip it back and forth
      this.onChangeHalfOrPostmark(!this.halfOrPostmark.dialogHalf, 'dialogHalf');
    },
    isVoidChecked() {
      if (!this.editedItem.isVoid) return;
      const retVoidFormat = commonVoidFunc({ ...this.editedItem });
      this.editedItem = retVoidFormat;
      this.syncTaxRowsToGrid(retVoidFormat.taxDetails);
    },
    /**
     * Pushes rows the header rewrote into LargePaymentList. In list mode the grid renders its
     * own copy of the rows (crud key 'largePaymentList'), so rewriting editedItem.taxDetails
     * alone leaves the grid showing - and re-emitting - the pre-change amounts. The old
     * dialogTaxDetails grid never needed this: it is v-model bound to editedItem.taxDetails.
     */
    syncTaxRowsToGrid(taxDetails) {
      const { largePaymentListRef } = this.$refs;
      if (largePaymentListRef && typeof largePaymentListRef.syncRowsFromPayment === 'function') {
        largePaymentListRef.syncRowsFromPayment(taxDetails);
        return;
      }
      // No grid to re-seed (dialogTaxDetails branch) - drop the cached server total so
      // totalSum falls back to summing the rewritten rows.
      this.serverTotalDue = null;
    },
    /**
     * Pushes a fee this dialog wrote onto a single tax row (Over/Under, Penny Round) into the
     * grid's footer figures. Nothing to do on the June Resale branch: dialogTaxDetails totals its
     * footer from the array it is v-model bound to, so it is already live.
     */
    applyGridFeeChange(updatedRow, feeDelta) {
      const { largePaymentListRef } = this.$refs || {};
      if (largePaymentListRef && typeof largePaymentListRef.applyHeaderFeeChange === 'function') {
        largePaymentListRef.applyHeaderFeeChange(updatedRow, feeDelta);
      }
    },
    checkPayDateFiscalYear(val) {
      if (!val || !this.isValidCompleteDate(val)) return;
      const changeYear = toFiscalYear(new Date(val));
      if (changeYear !== this.editedItem.fiscalYear) {
        this.showFiscalYearErrorMsg = true;
        this.editedItem.payDate = null;
      }
    },
    closeFiscalYearErrorMsg() {
      this.showFiscalYearErrorMsg = false;
    },
    checkIfTaxDetailsPosted() {
      if (((this.editedItem || {}).taxDetails || []).length) {
        const isAllPosted = this.editedItem.taxDetails.every((ft) => ft.posted);
        return isAllPosted;
      } return false;
    },
    /**
     * Where the cursor lands when the dialog opens, decided by 'Default cursor to Parcel ID'
     * (payment settings): on, the Parcel ID lookup; off, the Tax Year lookup, so the clerk can
     * key a Tax Yr / Tax ID pair straight away.
     */
    placeInitialCursor() {
      if (this.defaultCursorToParcelID) {
        this.focusParcelLookup();
        return;
      }
      this.focusTaxYearLookupOnOpen();
    },
    // Puts the cursor in the Parcel ID lookup when the setting asks for it. Deferred: the dialog
    // transition, the row autofocus and the payment details '+' autofocus all reach for the
    // cursor while the dialog is opening.
    focusParcelLookup() {
      if (!this.defaultCursorToParcelID) return;
      setTimeout(() => {
        if (this.$refs && this.$refs.parcelLookupRef) {
          this.$refs.parcelLookupRef.focus();
        }
      }, 100);
    },
    /**
     * Arranges for the cursor to come back to the header once the clerk has answered whatever
     * warning popups the tax-details grid raises - Continue or Cancel. LargePaymentList emits
     * warningsAnswered for this; dialogTaxDetails has no such event, hence the flag watch.
     * Only the June Resale branch still mounts that component, so this only runs there.
     */
    watchGridWarningsAnswered(gridRef) {
      if (this.disposeWarningWatch) this.disposeWarningWatch();
      this.disposeWarningWatch = onWarningPopupsAnswered({
        gridRef,
        watch: (getter, cb) => this.$watch(getter, cb),
        onAnswered: () => this.placeInitialCursor(),
      });
    },
    /**
     * The Tax Year lookup on open - the same 100ms deferral focusParcelLookup uses: the dialog
     * transition and the payment details '+' autofocus are both reaching for the cursor while
     * the dialog is still opening. On the June Resale branch there is a sharper reason too, with
     * the Parcel ID setting off dialogTaxDetails' per-row Tax Yr autofocus is ON
     * (taxYearAutoFocus is its inverse), so it has to be allowed to fire first or it takes the
     * cursor back into the grid.
     */
    focusTaxYearLookupOnOpen() {
      setTimeout(() => {
        this.focusAndSelectTaxYearLookup();
      }, 100);
    },
    /**
     * Puts the cursor in the Tax Year lookup with the year already selected, so whatever is keyed
     * next replaces it instead of being appended to it. The field's own @focus select only fires
     * when focus actually moves, and after a row lands the field is often already focused - Enter
     * and a barcode scan both leave it there - so the selection is made here as well.
     *
     * A scan is the case that needs it: the scanner keys 11 digits in one go, and appended to a
     * kept year that is a 15-digit value, which is not a scan any more and is dropped. Selected,
     * the scan replaces the year and splits into the pair as it should.
     */
    focusAndSelectTaxYearLookup() {
      const fieldRef = (this.$refs || {}).taxYearLookupRef;
      if (!fieldRef) return;
      fieldRef.focus();
      const input = fieldRef.$el ? fieldRef.$el.querySelector('input') : null;
      if (input && typeof input.select === 'function') input.select();
    },
    // The parcel resolved and its rows loaded - reported by the grid's return value, or by
    // dialogTaxDetails emitting parcelLookupConsumed - so the field empties for the next one.
    // A miss leaves it keyed for correcting.
    clearParcelLookup() {
      this.parcelLookupId = '';
    },
    /**
     * Keying the number picks the type: 1-7 is how a clerk says "refund", and the old grid's
     * dropdown answered to it. The digit sets the type and leaves the menu shut, so Tab moves
     * straight on to the Tax Year.
     * Explicit rather than left to Vuetify's own type-to-select, which matches on a prefix that
     * survives for a second: keying 2 and then 1 looked "21" up, found nothing, and left the
     * type on the 2 the clerk had just changed their mind about.
     */
    handleTaxTypeKeydown(event) {
      if (!event || event.ctrlKey || event.metaKey || event.altKey) return;
      const option = this.taxPaymentTypeOptions.find((opt) => opt.short === event.key);
      if (!option) return;
      event.preventDefault();
      this.taxTypeLookup = option.value;
    },
    /**
     * Hands the keyed Tax Yr + Tax ID to whichever grid is rendered, which resolves the pair and
     * loads the tax record as a row. The grid owns the rows, so - like the Parcel ID field -
     * this only holds the values and the spinner:
     *  - LargePaymentList resolves and appends it, raising the status-code / mortgage-code /
     *    no-taxes-due / pending-payment warnings in its own popup. This is every payment now
     *    bar one.
     *  - dialogTaxDetails, on the June Resale branch, stages it on a row and runs its own
     *    lookup, so its popups all appear as usual
     * Both fields call this on blur and on Enter: whichever is completed second is the one that
     * fires the lookup, and the first is a no-op because the pair is still incomplete.
     */
    async loadTaxRowByYearAndId() {
      // A scanner's trailing Enter / Tab reaches this before the scan debounce has split the
      // 11 digits into the pair - finish the split here rather than let the clearTimeout below
      // cancel it, or the pair is still incomplete when the guard runs and the scan is dropped.
      this.flushPendingBarCodeScan();
      clearTimeout(this.barcodeScanTimerId);
      const { dialogTaxDetailsRef, largePaymentListRef } = this.$refs || {};
      const grid = dialogTaxDetailsRef || largePaymentListRef;
      // guards an incomplete pair, a re-entry while the first lookup is in flight, and a dialog
      // with neither grid rendered yet
      if (!this.taxYearLookup || !this.taxIdLookup
        || this.taxRowLookupLoading || !grid) return;
      this.taxRowLookupLoading = true;
      try {
        // the band's Type rides along with the pair - the row is created as that type
        const lookupArgs = {
          taxYear: this.taxYearLookup,
          taxId: this.taxIdLookup,
          taxType: this.taxTypeLookup,
        };
        // set up before the lookup runs, so the popups it raises are seen opening
        if (dialogTaxDetailsRef) this.watchGridWarningsAnswered(dialogTaxDetailsRef);
        const added = dialogTaxDetailsRef
          // staged from out here rather than by a method on the grid - dialogTaxDetails is being
          // retired, so this whole branch goes when it does
          ? await loadTaxRowViaTaxDetailsGrid({
            ...lookupArgs,
            gridRef: dialogTaxDetailsRef,
            getTaxDetails: () => (this.editedItem || {}).taxDetails,
            setTaxDetails: (rows) => {
              this.editedItem = { ...this.editedItem, taxDetails: rows };
            },
            nextTick: () => this.$nextTick(),
          })
          : await largePaymentListRef.addTaxRowByYearAndId(lookupArgs);
        // cleared only on a hit, so a miss or a duplicate stays keyed for correcting. The year
        // goes back to the current tax year on every hit, the way the old system worked: a back
        // year is keyed for the one record that needs it, not carried into the next one. (The
        // Type is NOT reset with it - that stays on what the clerk picked.)
        if (added) {
          this.taxIdLookup = '';
          this.taxYearLookup = this.defaultTaxYearForLookup;
          // Where the cursor goes is the same question as on open, and the same answer the
          // warning-popup path already gives (warningsAnswered -> placeInitialCursor). A row
          // that lands without raising a popup used to end up somewhere else:
          // this.focusTaxIdLookup();
          this.placeInitialCursor();
        }
      } finally {
        this.taxRowLookupLoading = false;
      }
    },
    /**
     * Opens the grid's comment dialog, which writes the one comment onto every tax record on the
     * payment. The button that called this has gone back down into the grid's footer row, where
     * it calls addComments directly; this stays as the way to raise that dialog from out here.
     */
    openAddComment() {
      const { largePaymentListRef } = this.$refs || {};
      // guards the grid not being rendered yet, and the June Resale branch, which renders
      // dialogTaxDetails and has a comment button of its own
      if (largePaymentListRef && typeof largePaymentListRef.addComments === 'function') {
        largePaymentListRef.addComments();
      }
    },
    /**
     * A scanned barcode arrives as one 11-digit value in the Tax Year field - the first 4 are
     * the tax year and the rest the tax id. Debounced because the scanner keys the digits one
     * at a time, so this fires once the value has settled. Anything else is left alone for the
     * clerk to finish keying.
     */
    handleBarCodeScan(value) {
      clearTimeout(this.barcodeScanTimerId);
      if (scannedDigits(value).length !== 11) return;
      this.barcodeScanTimerId = setTimeout(() => {
        this.loadTaxRowByYearAndId();
      }, 300);
    },
    /**
     * Splits an 11-digit scan sitting in the Tax Year field into the pair, if one is there.
     * A no-op on anything else, so it is safe as the first line of the lookup - a keyed
     * Tax Yr / Tax ID pair, or a year part-way through being typed, is left alone.
     */
    flushPendingBarCodeScan() {
      const scanned = scannedDigits(this.taxYearLookup);
      if (scanned.length !== 11) return;
      this.taxYearLookup = scanned.slice(0, 4);
      this.taxIdLookup = scanned.slice(4);
    },
    // back to the Tax Year field after a row lands, so the next one can be keyed straight in
    focusTaxYearLookup() {
      this.$nextTick(() => {
        this.focusAndSelectTaxYearLookup();
      });
    },
    // with the year prefilled, the Tax ID is the only field left to key
    focusTaxIdLookup() {
      this.$nextTick(() => {
        if (this.$refs && this.$refs.taxIdLookupRef) {
          this.$refs.taxIdLookupRef.focus();
        }
      });
    },
    /**
     * Fills the Tax Year lookup with the default on open, without overwriting a year the clerk
     * (or a barcode scan) already put there. Only the open paths call this - a record landing
     * resets the year outright instead, in loadTaxRowByYearAndId.
     */
    seedTaxYearLookup() {
      if (this.taxYearLookup) return;
      this.taxYearLookup = this.defaultTaxYearForLookup;
    },
    // Typing drives the lookup - it fires once the user pauses, same debounce the tax search
    // criteria fields use. A miss stays silent on this path: the partial parcel keyed on the way
    // to the full one is not an error worth flashing. Enter and leaving the field still run the
    // lookup at once (and report a miss), so nobody has to wait out the timer.
    parcelLookupByTimeOut() {
      clearTimeout(this.parcelLookupTimerId);
      this.parcelLookupTimerId = setTimeout(() => {
        this.loadUnpaidsByParcelId({ silentWhenMissing: true });
      }, SEARCH_DEBOUNCE_TIMER);
    },
    /**
     * Parcel ID field in the Add Tax Records band. Lookup control only - the value is never
     * stored on the payment; it just resolves the parcel to its unpaid tax records and loads all
     * of them as rows. Whichever grid is rendered owns the rows, so this hands the keyed value to
     * that one and only holds the spinner and the debounce:
     *  - LargePaymentList, which reports back whether anything was added
     *  - dialogTaxDetails on the June Resale branch, which clears the field by emitting
     *    parcelLookupConsumed
     */
    async loadUnpaidsByParcelId({ silentWhenMissing = false } = {}) {
      // an explicit enter / blur wins over a lookup the debounce still has queued
      clearTimeout(this.parcelLookupTimerId);
      const { dialogTaxDetailsRef, largePaymentListRef } = this.$refs || {};
      const grid = dialogTaxDetailsRef || largePaymentListRef;
      // guards a blur straight after the enter key or the debounce already fired (the field is
      // cleared by then), a re-entry while the first lookup is still in flight, and a dialog
      // with neither grid rendered yet
      if (!this.parcelLookupId || this.parcelLookupLoading || !grid) return;
      this.parcelLookupLoading = true;
      try {
        if (dialogTaxDetailsRef) {
          // the unpaids load raises the same popups, so the cursor comes back the same way
          this.watchGridWarningsAnswered(dialogTaxDetailsRef);
          await dialogTaxDetailsRef.loadUnpaidsByParcelId({
            parcelId: this.parcelLookupId,
            silentWhenMissing,
          });
          return;
        }
        const added = await largePaymentListRef.addUnpaidRowsByParcelId({
          parcelId: this.parcelLookupId,
          silentWhenMissing,
          // every record the parcel loads is created as the type the band is set to
          taxType: this.taxTypeLookup,
        });
        // cleared only on a hit, so a miss leaves it keyed for correcting - same as the
        // dialogTaxDetails path, which clears via parcelLookupConsumed
        if (added) this.clearParcelLookup();
      } finally {
        this.parcelLookupLoading = false;
      }
    },
    isValidCompleteDate(dateStr) {
      // Check if date string is complete and valid (MM-DD-YYYY format)
      if (!dateStr || typeof dateStr !== 'string') {
        return false;
      }

      // Check for MM-DD-YYYY pattern (complete date)
      const datePattern = /^\d{1,2}-\d{1,2}-\d{4}$/;
      if (!datePattern.test(dateStr)) {
        return false;
      }

      // Try to parse the date to ensure it's valid
      try {
        const parts = dateStr.split('-');
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Basic validation
        if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
          return false;
        }

        // Create date object to validate
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year
          && date.getMonth() === month - 1
          && date.getDate() === day;
      } catch (e) {
        return false;
      }
    },
    handlePenDateInput(val) {
      // Ensure the penalty date is updated in the store immediately
      // Get current item from store
      const currentItem = this.item(this.baseCrudKey);
      // Force update the store with new penalty date
      this.setItem([this.baseCrudKey, {
        ...currentItem,
        penaltyDate: val,
      }]);
      // Then call the existing handlePenChange
      this.handlePenChange(val);
    },
    handlePenChange(val) {
      clearTimeout(this.delayTimerId);
      if ((val && (val).includes('-')) || !val) {
        this.delayTimerId = setTimeout(async () => {
          // For pick list payments, recalculate penalties locally for all records
          if (this.baseCrudKey === 'pickListMakePaymentDialog'
            && this.$refs.largePaymentListRef
            && !this.editedItem.paymentNumber) {
            // Validate that we have a complete date before recalculating
            if (!val || !this.isValidCompleteDate(val)) {
              return; // Don't recalculate with incomplete date
            }

            // Call the new method to recalculate penalties locally
            // Commented out with LargePaymentList's "Add Tax Details to Payment" card: the
            // method only ever walked that card's staged newTaxDetailsToAdd rows, so with the
            // card gone it returned immediately anyway. The early return below is unchanged.
            // this.$refs.largePaymentListRef.recalculatePenaltiesForPicklist(val);
            return; // Exit early, local recalculation handled
          }

          const selectedItems = (this.editedItem.taxDetails || [])
            .filter((item) => item.selected);
          if (selectedItems.length > 0) {
            this.calculatePenaltyForSelectedRecords(selectedItems);
            return;
          }

          // }

          // Update penalty for last added tax details record only (when no records are selected)
          // eslint-disable-next-line no-unreachable
          const { penaltyDate, penaltyRate } = this.taxSettingItem;
          const taxDetails = [...(this.editedItem.taxDetails || [])];
          // Find the last added (newest) tax details record that is not posted
          let lastAddedIndex = -1;
          // eslint-disable-next-line no-plusplus
          for (let i = taxDetails.length - 1; i >= 0; i--) {
            if (!taxDetails[i].posted && taxDetails[i].totalTax) {
              lastAddedIndex = i;
              break;
            }
          }

          // Update penalty only for the last added record
          if (lastAddedIndex >= 0) {
            const item = taxDetails[lastAddedIndex];
            const isExemptManufacturing = (item.statusCode || [])
              .includes(exemptManufacturingStatusCode);
            const updatedItem = { ...item };
            const isItAExemptPayment = updatedItem.isExempt;
            let returnedVal = { penaltyAmt: amountFormat(0) };

            if (!isItAExemptPayment) {
              returnedVal = penaltyCalculation(
                val, updatedItem.penaltyStartDate, penaltyDate, updatedItem.totalTax,
                updatedItem.totalPaid, 0, penaltyRate, updatedItem.taxTotalDue,
                updatedItem.taxYear, (item.statusCode || []), this.taxSettingItem,
              );
            }

            updatedItem.penaltyAmt = amountFormat(returnedVal.penaltyAmt);

            // Update feelist based on effective date
            let totalFee = updatedItem.totalFees;
            if ((updatedItem.allFeesList || []).length && !isExemptManufacturing) {
              // eslint-disable-next-line max-len
              const { feeDet, feesAmt } = getFeeListBasedOnEffeciveDate(updatedItem.allFeesList, val);
              totalFee = feesAmt;
              updatedItem.fees = feeDet;
              updatedItem.initialFees = feeDet;
            }
            updatedItem.totalFees = isExemptManufacturing ? 0 : amountFormat(totalFee);

            // Update total amount
            const totalCaclAmt = amountToNumber(returnedVal.penaltyAmt)
              + amountToNumber(updatedItem.taxAmt) + amountToNumber(totalFee);
            updatedItem.total = amountFormat(amountToNumber(totalCaclAmt || 0));

            // Update only the last added record in the array
            taxDetails[lastAddedIndex] = updatedItem;
          }

          this.editedItem.taxDetails = taxDetails;
        }, SEARCH_DEBOUNCE_TIMER);
      }
    },
    onChangeHalfOrPostmark(val, whichOne) {
      let isItNull = false;
      if (val) isItNull = val;
      if (whichOne === 'dialogPostmark') {
        if (val) {
          // Save the pre-toggle value so turning postmark back off can restore it
          // exactly, rather than re-deriving it and risking drift.
          this.prePostmarkPenaltyDate = this.editedItem.penaltyDate;
          const penDate = getPreviousPenaltyPeriodEnd(
            this.editedItem.penaltyDate, this.taxSettingItem.penaltyDate,
          );
          this.handlePenChange(penDate);
          this.editedItem.penaltyDate = toShortDate(penDate);
        } else {
          const penDate = this.prePostmarkPenaltyDate || toShortDate();
          this.prePostmarkPenaltyDate = null;
          this.handlePenChange(penDate);
          this.editedItem.penaltyDate = toShortDate(penDate);
        }
      }
      this.setHalfOrPostmark({ res: isItNull, whichOne });
    },
    getPaidBy() {
      const item = this.editedItem;
      if (item.paymentDetails && item.paymentDetails.length) {
        return item.paymentDetails[0].paidBy;
      }
      return '';
    },
    getTaxFeeDescription(feeNumber) {
      const match = (this.taxFeeMasterData || []).find((ft) => ft.feeNumber === feeNumber);
      return match ? match.feeDescription : null;
    },
    /**
     * Writes `mergedFees` onto the FIRST tax detail - initialFees too, so no audit record is
     * created - re-totals that row, syncs the large-payment store when it is in play, and
     * returns the payment's recalculated total due.
     *
     * Shared by the two buttons that balance a payment with a fee (Over/Under and Penny
     * Round); each settles change due / remaining balance its own way afterwards.
     */
    commitFirstTaxDetailFees(mergedFees) {
      // Only the paginated large list drives totals off the server; elsewhere totalSum is
      // computed live from taxDetails. Pinning serverTotalDue in that second case froze the
      // total, so a later edit (deleting this very fee in Edit Fees) left the screen showing
      // the pre-edit figure - and the Penny Round button judging against it.
      const isServerDriven = this.serverTotalDue !== null;
      const item = this.editedItem.taxDetails[0];
      const priorFeeAmt = amountToNumber(item.totalFees);
      this.editedItem.taxDetails[0].initialFees = mergedFees;
      this.editedItem.taxDetails[0].fees = mergedFees;

      const feeAmt = (mergedFees || [])
        .reduce((accumulator, itm) => accumulator + amountToNumber(itm.feeAmount), 0);
      this.editedItem.taxDetails[0].totalFees = amountToNumber(feeAmt);
      // calculate totalDue
      const totalCaclAmt = amountToNumber(item.penaltyAmt)
          + amountToNumber(item.taxAmt) + amountToNumber(feeAmt);
      this.editedItem.taxDetails[0].total = amountFormat(totalCaclAmt || 0);

      // Sync updates with LargePaymentList store if it's being used
      const isUsingLargePaymentList = this.checkIfTaxDetailsPosted()
        || this.baseCrudKey === 'pickListMakePaymentDialog';
      if (isUsingLargePaymentList) {
        // Update the largePaymentList store items
        this.setItems(['largePaymentList', [...this.editedItem.taxDetails]]);
        // Also update the parent store item
        this.setItem([this.baseCrudKey, { ...this.editedItem }]);
      }

      // The grid's footer - its Totals row and Running Total - is driven by figures it keeps
      // itself, not by summing the rows it is showing, so writing the fee onto taxDetails above
      // leaves it on the pre-fee amount however the rows got there. This runs for EVERY payment:
      // a new one renders the same grid, and the two syncs above only refresh the ROWS, which the
      // footer does not read. Without it Penny Round left the clerk with a Totals row and a
      // Running Total that disagreed with Total Due and Change Due.
      this.applyGridFeeChange(this.editedItem.taxDetails[0], feeAmt - priorFeeAmt);

      // Recalculate totals - reset serverTotalDue to force recalculation from taxDetails
      this.serverTotalDue = null;

      // Recalculate total from all taxDetails
      const newTotalSum = this.editedItem.taxDetails
        .reduce((acc, taxItem) => {
          if (!taxItem.taxType || taxItem.taxType === 'TAXPAYMENT') {
            return acc + amountToNumber(taxItem.total || 0);
          }
          return acc;
        }, 0);

      // Hand the recalculated value back to the server-driven total, or leave it null so
      // totalSum keeps tracking taxDetails as the clerk edits them.
      this.serverTotalDue = isServerDriven ? newTotalSum : null;
      return newTotalSum;
    },
    overUnderPayClicked() {
      const feeNumber = this.taxSettingItem.overUnderFee || null;
      const feeDesc = this.getTaxFeeDescription(feeNumber);
      if (this.editedItem.taxDetails && this.editedItem.taxDetails.length) {
        // only need the difference fee to  1st tax-record
        const prevTaxDet = this.editedItem.taxDetails[0].fees || [];
        const feeObj = {
          feeAmount: amountToNumber(this.remainingBalance),
          feeDescription: feeDesc,
          feeMongoId: null,
          feeNumber,
        };
        const newTotalSum = this.commitFirstTaxDetailFees([...prevTaxDet, feeObj]);

        // Recalculate remaining balance and hide the over/under button
        this.remainingBalance = amountToNumber(this.totalPaidMain) - newTotalSum;
        this.showOverUnder = false;
        // Clerk chose Over/Under instead of change due - clear any auto-set change amount
        this.editedItem.cashChange = 0;

        // Force UI update
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    // Oklahoma no longer allows pennies in the change handed back, so a cash total that is not
    // on a nickel gets a small +/- fee (like Over/Under, but with a computed amount) to move it
    // onto one. The fee goes on the FIRST tax detail; the receipt prints it as a fee line.
    pennyRoundClicked() {
      if (!this.editedItem.taxDetails || !this.editedItem.taxDetails.length) return;
      const feeNumber = this.getPennyRoundingFeeNumber();
      // Belt and braces - the button is already hidden when the county configured no fee.
      if (!feeNumber) return;

      // Re-derive from the total WITHOUT any penny fee this dialog already added: if the amount
      // due moved after the first click, the stale fee is part of the current total, so leaving
      // it in place would round against an amount that already carries a correction.
      const prevTaxDet = this.editedItem.taxDetails[0].fees || [];
      const isPriorPennyFee = (ft) => ft.feeNumber === feeNumber && ft.feeCreatedAtpayment;
      const keptFees = prevTaxDet.filter((ft) => !isPriorPennyFee(ft));
      const priorPennyAmt = prevTaxDet.filter(isPriorPennyFee)
        .reduce((acc, ft) => acc + amountToNumber(ft.feeAmount), 0);
      const baseTotal = amountToNumber(this.totalSum) - priorPennyAmt;

      const feeAmount = pennyRoundingFeeAmount(baseTotal);
      const mergedFees = feeAmount === 0 ? keptFees : [...keptFees, {
        feeAmount,
        feeDescription: this.getTaxFeeDescription(feeNumber),
        feeMongoId: null,
        feeNumber,
        feeCreatedAtpayment: true,
      }];

      this.commitFirstTaxDetailFees(mergedFees);

      // Re-run the payment-line evaluation so Change Due lands on the rounded total, and so
      // Over/Under is judged against that rounded total rather than the original one.
      this.totalPaymentAmount(this.totalPaidMain);

      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    // Whatever fee the county picked in Tax Settings -> Penny Rounding Fee # is the fee the
    // button writes. Null when the setting is empty, which suppresses the button entirely
    // rather than inventing a fee number the county never chose. Tax Settings pre-fills the
    // seeded 555 (see applyDefaultFeeNumbers), so "empty" means a county that never set it.
    getPennyRoundingFeeNumber() {
      return (this.taxSettingItem || {}).pennyRoundingFee || null;
    },
    // Cash change can no longer include pennies, so Penny Round is offered only on a plain
    // cash tender whose total is off a nickel and which still has change to hand back.
    evaluatePennyRound() {
      const paymentDetails = (this.editedItem || {}).paymentDetails || [];

      // Straight cash only. A row with no type picked yet is ignored so an empty placeholder
      // does not suppress the button, but a second *typed* row - cash + check, or two cash
      // rows - does: the tender is no longer a single cash payment.
      const tenders = paymentDetails.filter((ft) => ft.paymentType);
      const isCashOnly = tenders.length === 1 && tenders[0].paymentType === 'CASH';

      // Summed here rather than read off totalPaidMain, which is only refreshed by the
      // totalPaymentAmount emit - this method also runs from the paymentDetails watcher.
      const paid = paymentDetails.reduce((acc, ft) => acc + amountToNumber(ft.amount), 0);
      // An exact tender hands back no change, so there are no pennies to avoid.
      const isSettled = Math.round((paid - amountToNumber(this.totalSum)) * 100) === 0;

      // No fee configured means there is nothing to book the rounding against.
      const hasConfiguredFee = Boolean(this.getPennyRoundingFeeNumber());

      // Tax Settings -> Enable Penny Rounding. Only an explicit false turns it off: the key is
      // absent on every county that has not saved the tab since it was added, and those are the
      // counties already running penny rounding, so missing has to keep meaning on.
      const isEnabled = (this.taxSettingItem || {}).enablePennyRounding !== false;

      this.showPennyRound = isEnabled && isCashOnly && !isSettled && hasConfiguredFee
        && needsPennyRounding(this.totalSum);
    },
    overpaymentClicked() {
      const overpaymentAmt = Math.abs(amountToNumber(this.remainingBalance));
      if (overpaymentAmt > 0 && this.editedItem.paymentDetails) {
        const checkIndex = this.editedItem.paymentDetails
          .findIndex((pd) => pd.paymentType === 'CHECK');
        if (checkIndex >= 0) {
          // Use spread to ensure Vue 2 reactivity is triggered
          const updatedPaymentDetails = this.editedItem.paymentDetails.map((pd, i) => (
            i === checkIndex ? { ...pd, overpaymentAmt } : pd
          ));
          this.editedItem = { ...this.editedItem, paymentDetails: updatedPaymentDetails };
        }
        this.showOverpayment = false;
      }
    },
    totalPaymentAmount(val) {
      this.totalPaidMain = amountFormat(val);
      const item = this.editedItem;

      const isThereCashPay = (item.paymentDetails || []).filter((ft) => ft.paymentType === 'CASH');
      const isThereCheckPay = (item.paymentDetails || []).filter((ft) => ft.paymentType === 'CHECK');
      const isPaidGreaterThanTotalSum = (amountToNumber(this.totalPaidMain))
      > (amountToNumber(this.totalSum));
      const alternateOverpayment = this.paymentSettingItem.alternateOverpaymentHandling;
      const { allowChangeDueOnCheckPayments } = this.paymentSettingItem;
      const overUnderFeeLimit = amountToNumber(this.taxSettingItem.overUnderLimit);
      const remainBalForLimit = amountToNumber(this.totalPaidMain) - amountToNumber(this.totalSum);
      const isWithinOverUnderLimit = (remainBalForLimit !== 0)
        && (remainBalForLimit >= -(overUnderFeeLimit) && remainBalForLimit <= overUnderFeeLimit);

      this.evaluatePennyRound();

      // cash overpayment always gives physical change back - no over/under alternative
      if (isThereCashPay.length && isPaidGreaterThanTotalSum) {
        const cashChange = amountFormat(amountToNumber(this.totalPaidMain)
        - amountToNumber(this.totalSum));
        this.editedItem.cashChange = cashChange;
        this.showOverUnder = false;
        this.showOverpayment = false;
      } else if (allowChangeDueOnCheckPayments && isThereCheckPay.length && isPaidGreaterThanTotalSum) {
        // County allows change due on checks, but the clerk may still choose Over/Under
        // for this particular check instead of giving change - keep the button available.
        const cashChange = amountFormat(amountToNumber(this.totalPaidMain)
        - amountToNumber(this.totalSum));
        this.editedItem.cashChange = cashChange;
        this.remainingBalance = remainBalForLimit;
        this.showOverUnder = isWithinOverUnderLimit;
        this.showOverpayment = false;
      } else if (alternateOverpayment && isThereCheckPay.length && isPaidGreaterThanTotalSum) {
        // Alternate overpayment handling: show Overpayment button for check overpayments
        this.editedItem.cashChange = 0;
        const remainBal = amountToNumber(this.totalPaidMain) - amountToNumber(this.totalSum);
        this.remainingBalance = remainBal;
        // Re-show the button if no overpayment recorded yet, or if the amount has changed
        const expectedOverpayment = Math.abs(remainBal);
        this.showOverpayment = !this.overpaymentAmt
          || Math.abs(this.overpaymentAmt - expectedOverpayment) > 0.009;
        this.showOverUnder = false;
      } else {
        // use to show and hide over/under payment status
        this.editedItem.cashChange = 0;
        this.showOverpayment = false;
        this.remainingBalance = remainBalForLimit;
        this.showOverUnder = isWithinOverUnderLimit;
      }
    },
    handleTotalChanged(newTotal) {
      // Update the parent's tax details to reflect changes from LargePaymentList
      // The totalSum computed property will automatically recalculate based on taxDetails
      if (this.editedItem.taxDetails && newTotal !== undefined) {
        this.totalPaymentAmount(this.editedItem.totalPaid || 0);
        // Trigger reactivity to ensure totalSum reflects the changes
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    // When the clerk changes/clicks a Payment Info field (payment type, amount, paid by,
    // or the + button), commit any pending "Add Tax Details" row so it isn't lost
    // if the clerk moves on without clicking Add to List.
    // Still bound to dialogPaymentDetails' paymentFieldActivity, so the handler has to exist -
    // but there is nothing to commit now that LargePaymentList's "Add Tax Details to Payment"
    // card is commented out. The typeof guard already made this a no-op.
    handlePaymentFieldActivity() {
      // const { largePaymentListRef } = this.$refs;
      // if (largePaymentListRef
      //   && typeof largePaymentListRef.addToList === 'function'
      //   && largePaymentListRef.hasValidItemsToAdd) {
      //   largePaymentListRef.addToList();
      // }
    },
    handleServerTotalsChanged(serverTotals) {
      // Update server totals from LargePaymentList for accurate display across all pages
      if (serverTotals && serverTotals.totalRecords > 0) {
        this.serverTotalDue = serverTotals.totalDue;
      }
    },
    handleTaxDetailsChanged(updatedTaxDetails) {
      // Update the editedItem.taxDetails with the changes from LargePaymentList
      // This ensures that when the save button is pressed, the upsert API receives the correct data
      if (updatedTaxDetails && Array.isArray(updatedTaxDetails)) {
        // Create a new object to ensure reactivity
        let merged = {
          ...this.editedItem,
          taxDetails: [...updatedTaxDetails],
        };
        // Void All is one-way (the checkbox is disabled once ticked), so no row event - an
        // edit, a newly added row, a selection change - may bring live amounts back into a
        // payment the clerk already voided. Re-voiding is idempotent: commonVoidFunc skips
        // rows that are already void or posted.
        if (merged.isVoid) {
          merged = commonVoidFunc(merged);
          // Deferred: the callers that emit taxDetailsChanged (addToList,
          // handleTaxDetailChanged) go on to rewrite the grid's totals after the emit
          // returns, and would overwrite a synchronous re-seed.
          this.$nextTick(() => this.syncTaxRowsToGrid(merged.taxDetails));
        }
        this.editedItem = merged;
        // Also update the store directly to ensure consistency
        this.setItem([this.baseCrudKey, this.editedItem]);

        // Trigger reactivity to ensure totalSum reflects the changes
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },
    handleTaxDetailsSelectionChange(selectedItems) {
      // Store selected item IDs to preserve selection across data updates
      this.selectedTaxDetailIds = (selectedItems || []).map((item) => item._id);
      this.hasSelectedTaxDetails = selectedItems && selectedItems.length > 0;

      // Calculate penalty for all selected tax details records based on penalty date
      if (this.hasSelectedTaxDetails) {
        this.calculatePenaltyForSelectedRecords(selectedItems);
      }
    },
    calculatePenaltyForSelectedRecords(selectedItems) {
      const { penaltyDate } = this.editedItem;
      if (!penaltyDate) {
        return; // No penalty date set, skip calculation
      }

      const { penaltyDate: settingPenaltyDate, penaltyRate } = this.taxSettingItem;

      // Update penalty for each selected record
      const updatedTaxDetails = [...this.editedItem.taxDetails];

      selectedItems.forEach((selectedItem) => {
        // Find the index of this item in the tax details array
        const itemIndex = updatedTaxDetails.findIndex((item) => item._id === selectedItem._id);
        if (itemIndex >= 0 && !updatedTaxDetails[itemIndex].posted) {
          const item = updatedTaxDetails[itemIndex];
          const isExemptManufacturing = (item.statusCode || [])
            .includes(exemptManufacturingStatusCode);
          const updatedItem = { ...item };
          const isItAExemptPayment = updatedItem.isExempt;
          let returnedVal = { penaltyAmt: amountFormat(0) };

          if (!isItAExemptPayment && item.totalTax) {
            returnedVal = penaltyCalculation(
              penaltyDate, updatedItem.penaltyStartDate, settingPenaltyDate, updatedItem.totalTax,
              updatedItem.totalPaid, 0, penaltyRate, updatedItem.taxTotalDue,
              updatedItem.taxYear, (item.statusCode || []), this.taxSettingItem,
            );

            updatedItem.penaltyAmt = amountFormat(returnedVal.penaltyAmt);

            // Update feelist based on effective date if needed
            let totalFee = updatedItem.totalFees;
            if ((updatedItem.allFeesList || []).length && !isExemptManufacturing) {
              const { feeDet, feesAmt } = getFeeListBasedOnEffeciveDate(
                updatedItem.allFeesList, penaltyDate,
              );
              totalFee = feesAmt;
              updatedItem.fees = feeDet;
              updatedItem.initialFees = feeDet;
            }
            updatedItem.totalFees = isExemptManufacturing ? 0 : amountFormat(totalFee);

            // Update total amount
            const totalCalcAmt = amountToNumber(returnedVal.penaltyAmt)
              + amountToNumber(updatedItem.taxAmt) + amountToNumber(totalFee);
            updatedItem.total = amountFormat(totalCalcAmt || 0);

            // Update the item in the array
            updatedTaxDetails[itemIndex] = updatedItem;
          }
        }
      });

      // Update the tax details array
      this.editedItem.taxDetails = updatedTaxDetails;
      this.$forceUpdate();
    },
    preserveSelectionState() {
      // Restore selection state for tax details based on stored IDs
      if (this.editedItem.taxDetails) {
        this.editedItem.taxDetails.forEach((item) => {
          // Set selected to true only if the item ID is in the stored list
          // Set selected to false if the item ID is NOT in the stored list
          // eslint-disable-next-line no-param-reassign
          item.selected = this.selectedTaxDetailIds.includes(item._id);
        });
        // Update the hasSelectedTaxDetails flag
        this.hasSelectedTaxDetails = this.selectedTaxDetailIds.length > 0;
      }
    },
    clearAllSelections() {
      // Clear all selections
      this.selectedTaxDetailIds = [];
      this.hasSelectedTaxDetails = false;
      if (this.editedItem.taxDetails) {
        this.editedItem.taxDetails.forEach((item) => {
          // eslint-disable-next-line no-param-reassign
          item.selected = false;
        });
      }
    },
  },
  watch: {
    'editedItem.payDate': function payDateWatcher(val) {
      this.checkPayDateFiscalYear(val);
    },
    isVisibleProp: {
      handler(newVal) {
        if (newVal) {
          this.payDateAtOpen = this.editedItem.payDate || null;
          // to force re-render the total paid field when the dialog is opened or closed
          this.changeKeyOnDialog += 1;
          // re-opens reuse the mounted component, so mounted() cannot place the cursor again
          this.seedTaxYearLookup();
          this.placeInitialCursor();
          // Sync totalPaidMain and recalc cashChange from editedItem when dialog opens
          // (e.g. 2nd+ open: list row may not have cashChange; totalPaymentAmount sets it)
          this.$nextTick(() => {
            if (this.editedItem && (this.editedItem._id || this.editedItem.paymentNumber != null)) {
              this.totalPaymentAmount(this.editedItem.totalPaid || 0);
            }
          });
        } else {
          this.changeKeyOnDialog = 1;
          // criteria left keyed on a closed dialog must not be waiting in the next payment
          clearTimeout(this.parcelLookupTimerId);
          clearTimeout(this.barcodeScanTimerId);
          if (this.disposeWarningWatch) this.disposeWarningWatch();
          this.parcelLookupId = '';
          this.taxYearLookup = '';
          this.taxIdLookup = '';
          // the type is sticky WITHIN a payment only - the next one starts at 1 Tax Payment
          this.taxTypeLookup = DEFAULT_TAX_PAYMENT_TYPE;
        }
      },
    },
    'editedItem.overUnderStatus': {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.showOverUnder = true;
        } else {
          this.showOverUnder = false;
        }
      },
    },
    'editedItem.taxDetails': {
      handler() {
        // Preserve selection state when tax details are updated
        this.$nextTick(() => {
          this.preserveSelectionState();
          // The total due just moved, so the nickel check has to be redone. Only the
          // showPennyRound flag is touched here - nothing inside editedItem - so this cannot
          // re-trigger the deep watcher it runs from.
          this.evaluatePennyRound();
        });
      },
      deep: true,
    },
    // Penny Round keys off the payment TYPE, but dialogPaymentDetails only emits
    // totalPaymentAmount from a computed that depends on each line's `amount`. Picking CASH on
    // a line that already has an amount (or none yet) moves no amount, so without this the
    // button would not appear until something unrelated changed. Sets only showPennyRound.
    'editedItem.paymentDetails': {
      handler() {
        this.evaluatePennyRound();
      },
      deep: true,
    },
    // Reset serverTotalDue when payment changes to ensure fresh calculation
    'editedItem.paymentNumber': {
      handler() {
        this.serverTotalDue = null;
      },
    },
    // Check if we're using dialogTaxDetails (not LargePaymentList) and reset serverTotalDue
    baseCrudKey: {
      handler(newVal) {
        // For non-picklist modes with paymentNumber === 0, we use dialogTaxDetails
        // which calculates totals locally, so reset serverTotalDue
        if (newVal !== 'pickListMakePaymentDialog'
          && (this.editedItem.paymentNumber || 0) === 0) {
          this.serverTotalDue = null;
        }
      },
      immediate: true,
    },
  },
};
</script>
<style scoped>
/* `.row` styled the three v-rows the header used to be. The header is its own card now
   (.payment-header-card) and none of its rows carry this class, so nothing reads it - kept
   rather than deleted in case a consumer still hands a .row down into this dialog.
.row {
  margin: -1.4rem !important;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
*/
.payment-dialog-content {
  border: 1px solid lightgray;
  padding: 1rem 0.5rem 0rem 0.5rem;
}

/* HEADER CARD - the payment's own information. Fields left, totals right. */
.payment-header-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 6px;
  padding: 0.75rem 1rem 0.5rem 1rem;
  margin-bottom: 1rem;
}
.payment-header-fields {
  flex: 1 1 auto;
  min-width: 0;
}
.payment-header-row + .payment-header-row {
  margin-top: 0.25rem;
}
/* the second row is controls rather than text fields, which carry their own
   label space - this gives it the equivalent breathing room */
.payment-header-row--flags {
  padding-bottom: 0.25rem;
}
/* the thin rule the mockup puts between the two toggles and the checkboxes */
.payment-header-rule {
  width: 1px;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.3);
}
.payment-header-penalty-date {
  min-width: 150px;
}
/* The two toggles' own size and casing now come from .binary-toggle in assets/style.css,
   which every segmented either/or control on the screen shares. */

/* TOTALS - read-only figures, so they are text and not inputs. */
.payment-header-totals {
  flex: 0 0 auto;
  width: 190px;
  align-self: stretch;
  background-color: rgba(25, 118, 210, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}
.payment-totals-legend {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  opacity: 0.6;
  margin-bottom: 0.25rem;
}
.payment-totals-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.1rem 0;
}
/* All three read the same - Total Due was set larger and bolder, which made it a
   different figure to the two under it and wrapped its own label onto two lines. */
.payment-totals-label {
  font-size: 0.75rem;
  opacity: 0.75;
  white-space: nowrap;
}
/* Bold: the three figures are what the clerk reads off this panel, and they are
   read against the labels beside them, so the weight is what separates the two. */
.payment-totals-value {
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

/* ADD TAX RECORDS - the band belongs to the list below it, not to the header
   above it, so it carries the list's own left/right margin and loses its bottom
   corners to sit flush on the grid. */
.record-list-card {
  width: 100%;
}
.add-tax-records-band {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  padding: 0.35rem 0.75rem 0.5rem 0.75rem;
  background-color: rgba(25, 118, 210, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-bottom: none;
  border-radius: 6px 6px 0 0;
}
.add-tax-records-legend {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex: 0 0 auto;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.15;
  color: #1976d2;
}
.add-tax-records-legend-text {
  white-space: nowrap;
}
.add-tax-records-field {
  flex: 0 1 140px;
  min-width: 90px;
}
.add-tax-records-field--year {
  flex: 0 1 100px;
  min-width: 70px;
}
/* The Type selector reads "2 Tax Refund", not a number, so it is wider than the
   fields beside it. */
.add-tax-records-field--type {
  flex: 0 1 170px;
  min-width: 130px;
}
/* Amber: a Type other than 1 Tax Payment is the thing most worth noticing in this
   band - it is sticky, so EVERY record keyed below it is created as that type until
   it is put back. */
.add-tax-records-field--alt >>> .v-input__slot {
  background-color: #fff8e1;
  border: 1px solid #ffa000;
  border-radius: 4px;
  padding: 0 0.35rem;
}
.add-tax-records-field--alt >>> .v-label {
  color: #a06000;
}
.add-tax-records-hint {
  flex: 1 1 auto;
  min-width: 140px;
  font-size: 0.6875rem;
  line-height: 1.2;
  opacity: 0.65;
}
.large-payment-wrapper {
  width: 100%;
  /* no gap: the Add Tax Records band above is the list's own top edge */
  margin-top: 0;
  clear: both;
  overflow: hidden;
}
.large-payment-wrapper >>> .v-container {
  max-width: 100%;
  padding: 0;
}
.large-payment-wrapper >>> .elevation-3 {
  margin: 0 0 0.5rem 0 !important;
}
/* .taxroll-table-wrapper is `width: 98% !important` app-wide, and with no auto
   margins the whole 2% came off the right - the grid stopped ~27px short of the
   Add Tax Records band sitting on top of it, so the band visibly overhung the
   list. Here the two are one card and share an edge, so the grid takes the full
   width. Scoped to this dialog: the 98% is every other grid's own business. */
.large-payment-wrapper >>> .taxroll-table-wrapper {
  width: 100% !important;
}
</style>
